import { Fragment, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAgentsStore } from '../../stores/agentsStore';
import type { AgentSummary } from '../../types/agents';
import { MarkdownView } from './MarkdownView';
import { useRecentFindingsQuery } from './RecentFindingsTable.queries';

interface RecentFindingsTableProps {
  agents: AgentSummary[];
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const days = Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export function RecentFindingsTable({ agents }: RecentFindingsTableProps) {
  const { findingsDocs, findingsLoading, loadFindingsDoc } = useAgentsStore();
  const [openRow, setOpenRow] = useState<string | null>(null);

  const query = useRecentFindingsQuery(agents);
  const rows = query.data ?? [];

  const toggle = (filename: string) => {
    if (openRow === filename) {
      setOpenRow(null);
      return;
    }
    setOpenRow(filename);
    if (!findingsDocs[filename] && !findingsLoading[filename]) {
      void loadFindingsDoc(filename);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Recent findings docs ({rows.length})
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No findings docs yet.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Filename</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell align="right">When</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((doc) => (
                <Fragment key={doc.filename}>
                  <TableRow hover>
                    <TableCell>
                      <Chip label={doc.agent} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                      {doc.filename}
                    </TableCell>
                    <TableCell align="right">{doc.size_kb} KB</TableCell>
                    <TableCell align="right">{formatRelative(doc.mtime)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => toggle(doc.filename)}>
                        {openRow === doc.filename ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                      <Collapse in={openRow === doc.filename}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          {findingsLoading[doc.filename] ? (
                            <CircularProgress size={20} />
                          ) : findingsDocs[doc.filename] ? (
                            <MarkdownView body={findingsDocs[doc.filename].body} maxHeight={600} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No content loaded.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
